import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useOutletContext } from 'react-router-dom';
import { ProductionOutletType } from '..';
import { TypeMainOutlet } from '../..';
import { GlobalTable, HeaderLine, Td, Th, Tr, FooterTr } from '../../../../components/styles/table-style';
import { ArrowDownCircle, ArrowUpCircle, Settings } from 'lucide-react';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import ApiService from '../../../../services/ApiService';
import { format } from 'date-fns';
import TableFiltersContext from '../../../../contexts/TableFiltersContext';
import { ExitOrEntry } from '../../Stock/MovementsTable/style';
import { StockMovement } from '../../Stock/MovementsTable/types';
import { TableContainer } from '../style';
import { ConfigMenuButton, ConfigMenuWrapper } from '../../SteelOrders/SteelOrdersTable/style';
import FieldsManager, { FieldsManagerType } from '../../../../components/FieldManager';

interface User {
  name: string;
}
interface ProductionMovement {
  id: number;
  ploomesOrderId: number;
  rawMaterialName: string;
  status: string;
  quantity: number;
  oldTotalWeight: number;
  newTotalWeight: number;
  oldLengthInMm: number;
  newLengthInMm: number;
  createdAt: string;
  orderName: string;
  updatedBy: User;
  updatedAt: string;
  [key: string]: any; 
}

const ProductionMovementTable: React.FC = () => {
  const { setTabIndex, fields, setFields, isdropdownOpen, setIsDropdownOpen, quantity, setQuantity, refresh } = useOutletContext<ProductionOutletType>();
  const { search } = useOutletContext<TypeMainOutlet>();
  const { movementFilters, movementTypeFilter } = useContext(TableFiltersContext);
  const [movements, setMovements] = useState<ProductionMovement[]>([]);
  const [totalMovements, setTotalMovements] = useState<number | null>(null);
  const [lastReturnedCount, setLastReturnedCount] = useState<number>(0);

  const columnMenuRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsDropdownOpen]);

  const normalizeMovements = (raw: any): ProductionMovement[] => {
    if (Array.isArray(raw)) {
      return raw as ProductionMovement[];
    }
    if (raw?.data && Array.isArray(raw.data)) {
      return raw.data as ProductionMovement[];
    }
    if (raw?.items && Array.isArray(raw.items)) {
      return raw.items as ProductionMovement[];
    }
    if (raw?.results && Array.isArray(raw.results)) {
      return raw.results as ProductionMovement[];
    }

    if (raw && typeof raw === 'object') {
      const firstArray = Object.values(raw).find((value) => Array.isArray(value)) as ProductionMovement[] | undefined;
      if (Array.isArray(firstArray)) {
        return firstArray;
      }
    }

    return [];
  };

  useEffect(() => {
    setTabIndex(1);
  }, [setTabIndex]);

  useEffect(() => {
    const fetchMovements = async () => {
      try {
        const parameters = {
          size: quantity,
          filters: movementFilters,
          global: search,
          movementType: movementTypeFilter
        };

        const response = await ApiService.movements.find(parameters);
          const rawData = response?.data;
        const extractedMovements = normalizeMovements(rawData);

        const returnedCount = extractedMovements.length;
        setLastReturnedCount(returnedCount);

        console.log('🔍 [MOVEMENT] Fetched movements:', {
          requestedQuantity: quantity,
          returnedCount: returnedCount,
          totalMovements: totalMovements,
          hasMore: returnedCount >= 10 && extractedMovements.length < (totalMovements || returnedCount)
        });

        setMovements(extractedMovements);
      } catch (error) {
        console.error('Erro ao buscar movimentações de produção:', error);
      }
    };

    fetchMovements();
  }, [quantity, movementFilters, search, movementTypeFilter, refresh]);

  useEffect(() => {
    const fetchTotalMovements = async () => {
      try {
        const response = await ApiService.movements.getTotal({ 
          filters: movementFilters,
          global: search,
          movementType: movementTypeFilter
        });
        
        if (response?.data != null) {
          const total = typeof response.data === 'number' ? response.data : response.data.total || response.data.count || null;
          setTotalMovements(total);
        }
      } catch (error) {
        console.error('Erro ao buscar total de movimentações:', error);
        setTotalMovements(null);
      }
    };

    fetchTotalMovements();
  }, [movementFilters, movementTypeFilter, search, refresh]);

  const formatNumber = (value: number) => {
    if (typeof value !== 'number') return '-';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const filteredData = (item: ProductionMovement) => {
    if (movementTypeFilter === "Manual") {
      return item.ploomesOrderId === 0 || item.ploomesOrderId === 1;
    }
    if (movementTypeFilter === "Pedido") {
      return item.ploomesOrderId !== 0 && item.ploomesOrderId !== 1;
    }
    return true;
  };

  const hasMore = useMemo(() => {
    if (totalMovements !== null) {
      const hasMoreData = movements.length < totalMovements && lastReturnedCount === quantity;
      console.log('🔍 [MOVEMENT] hasMore calculation:', {
        movementsLength: movements.length,
        quantity: quantity,
        lastReturnedCount: lastReturnedCount,
        totalMovements: totalMovements,
        hasMore: hasMoreData
      });
      return hasMoreData;
    }
    const hasMoreData = lastReturnedCount === quantity && lastReturnedCount > 0;
    console.log('🔍 [MOVEMENT] hasMore calculation (no total):', {
      movementsLength: movements.length,
      quantity: quantity,
      lastReturnedCount: lastReturnedCount,
      hasMore: hasMoreData
    });
    return hasMoreData;
  }, [movements.length, quantity, totalMovements, lastReturnedCount]);

    const handleIdClick = (id: number) => {
    if (id > 1) window.open(`https://app10.ploomes.com/deal/${id}`, '_blank');
  };
  const renderOrderCell = (item: StockMovement) => {
    const { ploomesOrderId, orderName } = item;
    const maxLength = 25;
    const truncatedOrderName = orderName && orderName.length > maxLength 
      ? orderName.slice(0, maxLength) + '...' 
      : orderName;

    if (ploomesOrderId === 0) {
      return (
        <ExitOrEntry title='Entrada' className="flex items-center gap-2 entry">
          <ArrowDownCircle color="green" size={20} />
          <span className='inline-flex' title={orderName}>{truncatedOrderName}</span>
        </ExitOrEntry>
      );
    }

    if (ploomesOrderId === 1) {
      return (
        <ExitOrEntry title='Saída'>
          <ArrowUpCircle color='red' size={20} />
          <span title={orderName}>{truncatedOrderName}</span>
        </ExitOrEntry>
      );
    }

    return (
      <span
        title={orderName}
        onClick={() => handleIdClick(ploomesOrderId)}
        style={{ cursor: 'pointer', color: 'blue' }}
      >
        {orderName.substring(0, 25)}...
      </span>
    );
  };

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <HeaderLine>
            {fields?.whichFieldsToShow?.filter(field => field.checked).map((field, index) => (
              <Th key={index}>{field.fieldName}</Th>
            )) || (
                <>
                  <Th>Pedido</Th>
                  <Th>Matéria prima</Th>
                  <Th>Status</Th>
                  <Th>Quantidade</Th>
                  <Th>Peso anterior</Th>
                  <Th>Peso novo</Th>
                  <Th>Comp. anterior</Th>
                  <Th>Comp. novo</Th>
                  <Th>Data</Th>
                </>
              )}
            <Th>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'inline-block' }}>Ações</div>
                <ConfigMenuWrapper ref={columnMenuRef}>
                  <ConfigMenuButton onClick={() => setIsDropdownOpen(prev => !prev)} title='Mostrar/ocultar colunas' style={{ color: '#bdbdbd' }}>
                    <Settings size={20} />
                  </ConfigMenuButton>
                  {isdropdownOpen && (
                    <FieldsManager
                      dropdownOpen={setIsDropdownOpen}
                      fields={fields}
                      setFields={setFields as React.Dispatch<React.SetStateAction<FieldsManagerType>>}
                    />
                  )}
                </ConfigMenuWrapper>
              </div>
            </Th>
          </HeaderLine>
        </thead>
        <tbody>
          {movements.filter(filteredData).map((item) => (
            <Tr key={item.id}
                        className={item.status === '410' ? 'yellowLine' : ''} 
                        title={
                item.updatedBy?.name && item.updatedAt 
                  ? `Atualizado por ${item.updatedBy.name} em ${
                    format(new Date(item.updatedAt), 'dd/MM/yyyy - HH:mm')}h`
                  : undefined
                        }
            >
              {fields?.whichFieldsToShow?.filter(field => field.checked).map((field, index) => (
                <Td key={index}>
                  {field.field === 'createdAt' ? (
                    format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm') + 'h'
                  ) : field.field === 'ploomesOrderId' ? (
                    renderOrderCell(item)
                  ) : typeof item[field.field as keyof ProductionMovement] === 'number' ? (
                    formatNumber(item[field.field as keyof ProductionMovement] as number)
                  ) : field.field === 'status' ? (
                    item[field.field] === '410' ? 'Desfeito' : 'Abatido'
                  ) : (
                    item[field.field as keyof ProductionMovement]
                  )}
                </Td>
              )) || (
                  <>
                    <Td>{item.orderName || item.ploomesOrderId}</Td>
                    <Td>{item.rawMaterialName}</Td>
                    <Td>{item.status === '410' ? 'Desfeito' : 'Abatido'}</Td>
                    <Td>{formatNumber(item.quantity)}</Td>
                    <Td>{formatNumber(item.oldTotalWeight)}</Td>
                    <Td>{formatNumber(item.newTotalWeight)}</Td>
                    <Td>{formatNumber(item.oldLengthInMm)}</Td>
                    <Td>{formatNumber(item.newLengthInMm)}</Td>
                    <Td>{format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm')}h</Td>
                  </>
                )}
              <Td />
            </Tr>
          ))}
          {movements.length === 0 && (
            <Tr>
              <Td colSpan={(fields?.whichFieldsToShow?.filter(field => field.checked).length || 9) + 1} style={{ textAlign: 'center', padding: '20px' }}>
                Nenhuma movimentação encontrada
              </Td>
            </Tr>
          )}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td
              colSpan={(fields?.whichFieldsToShow?.filter(field => field.checked).length || 9) + 1}
              style={{ textAlign: 'center', background: 'transparent', padding: '1rem 0', width: '100%' }}
            >
              <LoadMoreButton
                currentCount={movements.length}
                totalCount={totalMovements ?? undefined}
                onLoadMore={() => setQuantity((q) => q + 50)}
                hasMore={hasMore}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </StyledTable>
    </TableContainer>
  );
};

const StyledTable = styled(GlobalTable)`
  width: 100%;
  min-width: 100%;
  margin: 0 auto;
`;

export default ProductionMovementTable;